import { useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PALETTE_OPTIONS, DEFAULT_CUSTOM_COLORS } from "@/components/SettingsPanel";

// ── Colour slot (matches SettingsPanel.js) ────────────────────────────────────
function ColorSlot({ index, color, onChange }) {
  const inputRef = useRef(null);
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        title={`Colour ${index + 1} — click to change`}
        onClick={() => inputRef.current?.click()}
        className="h-9 w-9 rounded-lg border-2 border-white shadow ring-1 ring-black/10 transition hover:scale-110 hover:ring-[#57c1a9] focus:outline-none focus:ring-2 focus:ring-[#57c1a9]"
        style={{ backgroundColor: color }}
      />
      <input
        ref={inputRef}
        type="color"
        value={color}
        onChange={(e) => onChange(index, e.target.value)}
        className="sr-only"
        aria-label={`Colour ${index + 1}`}
      />
      <span className="text-[10px] text-[#7d8b96]">{index + 1}</span>
    </div>
  );
}

// ── Settings page card ────────────────────────────────────────────────────────
export default function SettingsWorkspacePanel({ settings, onUpdate }) {
  const customColors =
    settings.customColors && settings.customColors.length === 10
      ? settings.customColors
      : DEFAULT_CUSTOM_COLORS;

  const handleColorChange = (index, value) => {
    const next = [...customColors];
    next[index] = value;
    onUpdate("customColors", next);
  };

  const applyPreset = (colors) => {
    onUpdate("customColors", [...colors]);
  };

  return (
    <Card className="border-[#dddddd] bg-white shadow-sm" data-testid="settings-workspace-panel">
      <CardHeader>
        <CardTitle className="font-sans text-2xl text-[#323c44]">Reporting settings</CardTitle>
        <CardDescription className="font-sans text-sm leading-7 text-[#4a5b68]">
          Manage the reporting presentation from its own sector of the workspace.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">

        {/* ── Section names ───────────────────────────────────────────────── */}
        <div className="space-y-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Section names</span>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* MRR */}
            <div className="space-y-3">
              <label className="space-y-2 block">
                <span className="text-xs text-[#7d8b96]">MRR section title</span>
                <input
                  className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                  value={settings.title}
                  onChange={(e) => onUpdate("title", e.target.value)}
                  data-testid="settings-title-input"
                />
              </label>
              <label className="space-y-2 block">
                <span className="text-xs text-[#7d8b96]">MRR subtitle</span>
                <input
                  className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                  value={settings.subtitle}
                  onChange={(e) => onUpdate("subtitle", e.target.value)}
                  data-testid="settings-subtitle-input"
                />
              </label>
            </div>
            {/* ARR */}
            <div className="space-y-3">
              <label className="space-y-2 block">
                <span className="text-xs text-[#7d8b96]">ARR section title</span>
                <input
                  className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                  value={settings.arrTitle || ""}
                  placeholder="ARR Report"
                  onChange={(e) => onUpdate("arrTitle", e.target.value)}
                  data-testid="settings-arr-title-input"
                />
              </label>
              <label className="space-y-2 block">
                <span className="text-xs text-[#7d8b96]">ARR subtitle</span>
                <input
                  className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                  value={settings.arrSubtitle || ""}
                  placeholder="Total recurring revenue, broken down by product."
                  onChange={(e) => onUpdate("arrSubtitle", e.target.value)}
                  data-testid="settings-arr-subtitle-input"
                />
              </label>
            </div>
            {/* Revenue by Client */}
            <div className="space-y-3">
              <label className="space-y-2 block">
                <span className="text-xs text-[#7d8b96]">Revenue by Client title</span>
                <input
                  className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                  value={settings.revenueByClientTitle || ""}
                  placeholder="Revenue by Client"
                  onChange={(e) => onUpdate("revenueByClientTitle", e.target.value)}
                  data-testid="settings-rbc-title-input"
                />
              </label>
              <label className="space-y-2 block">
                <span className="text-xs text-[#7d8b96]">Revenue by Client subtitle</span>
                <input
                  className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                  value={settings.revenueByClientSubtitle || ""}
                  placeholder="Per-company ARR across the selected period."
                  onChange={(e) => onUpdate("revenueByClientSubtitle", e.target.value)}
                  data-testid="settings-rbc-subtitle-input"
                />
              </label>
            </div>
          </div>
        </div>

        {/* ── Currency + decimals + FY ─────────────────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-3">
          <label className="space-y-2" data-testid="settings-currency-group">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Currency</span>
            <select
              className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
              value={settings.currency}
              onChange={(e) => onUpdate("currency", e.target.value)}
              data-testid="settings-currency-select"
            >
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
            </select>
          </label>
          <label className="space-y-2" data-testid="settings-decimals-group">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Decimal places</span>
            <input
              type="number"
              min="0"
              max="4"
              className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
              value={settings.decimalPlaces}
              onChange={(e) => onUpdate("decimalPlaces", Number(e.target.value))}
              data-testid="settings-decimals-input"
            />
          </label>
          <label className="space-y-2" data-testid="settings-fy-group">
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Financial year starts</span>
            <select
              className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
              value={settings.fiscalYearStartMonth ?? 7}
              onChange={(e) => onUpdate("fiscalYearStartMonth", Number(e.target.value))}
              data-testid="settings-fy-select"
            >
              {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
            <p className="text-xs text-[#7d8b96]">Used for the "Closed won – this financial year" metric.</p>
          </label>
        </div>

        {/* ── Toggles ───────────────────────────────────────────────────────── */}
        <div className="grid gap-3 lg:grid-cols-3" data-testid="settings-toggle-group">
          {[
            ["roundWholeNumbers", "Round to whole numbers"],
            ["showTotals",        "Show totals above bars"],
            ["showLegend",        "Show chart legend"],
          ].map(([key, label]) => (
            <label
              key={key}
              className="flex items-center justify-between rounded-md border border-[#dddddd] px-4 py-3 text-sm text-[#323c44]"
              data-testid={`settings-toggle-${key}`}
            >
              <span>{label}</span>
              <input
                type="checkbox"
                checked={settings[key]}
                onChange={(e) => onUpdate(key, e.target.checked)}
                data-testid={`settings-toggle-${key}-checkbox`}
              />
            </label>
          ))}
        </div>

        {/* ── Date format ───────────────────────────────────────────────────── */}
        <div className="space-y-3" data-testid="settings-date-format-group">
          <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Date format</span>
          <select
            className="h-11 w-full rounded-md border border-[#dddddd] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
            value={settings.dateFormat}
            onChange={(e) => onUpdate("dateFormat", e.target.value)}
            data-testid="settings-date-format-select"
          >
            <option value="short">MMM YYYY</option>
            <option value="compact">MMM YY</option>
            <option value="long">MMMM YYYY</option>
          </select>
        </div>

        {/* ── Chart colours ─────────────────────────────────────────────────── */}
        <div className="space-y-4" data-testid="settings-palette-group">
          <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Chart colours</span>

          {/* 10 individual pickers */}
          <div className="grid grid-cols-10 gap-2">
            {customColors.map((color, i) => (
              <ColorSlot key={i} index={i} color={color} onChange={handleColorChange} />
            ))}
          </div>

          {/* Quick-start presets */}
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#7d8b96]">Quick start</p>
            <div className="grid gap-2 md:grid-cols-3">
              {PALETTE_OPTIONS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.colors)}
                  className="flex items-center justify-between rounded-md border border-[#dddddd] bg-white px-4 py-3 text-left transition hover:border-[#57c1a9] hover:bg-[#e8faf5]"
                  data-testid={`palette-option-${preset.id}`}
                >
                  <span className="text-sm font-semibold text-[#323c44]">{preset.name}</span>
                  <span className="flex gap-1.5">
                    {preset.colors.map((c, i) => (
                      <span key={i} className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: c }} />
                    ))}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

      </CardContent>
    </Card>
  );
}
